/**
 * BPO Contact - Brand Configuration
 *
 * Brand Identity: Pure-play multilingual Contact Center & CX outsourcing
 * Tagline: "Your multilingual contact center — customer service,
 * technical support & back-office, in multiple languages."
 *
 * Color Philosophy:
 * - Deep Navy / Royal Blue: trust, reliability, enterprise-grade
 * - Vibrant Coral / Orange: energy, action, CTA accent
 * - Clean white surfaces
 */

export const BRAND = {
  // Core brand identity
  name: 'BPO Contact',
  shortName: 'BPO Contact',
  tagline:
    'Your multilingual contact center — customer service, technical support & back-office, in multiple languages.',
  taglineFr:
    'Votre centre de contact multilingue — service client, support technique & back-office, en plusieurs langues.',

  // Domain & URLs
  domain: 'bpo-contact.com',
  url: 'https://bpo-contact.com',

  // Contact information
  email: {
    contact: 'contact@bpo-contact.com',
    sales: 'sales@bpo-contact.com',
    support: 'support@bpo-contact.com',
    press: 'press@bpo-contact.com',
    careers: 'careers@bpo-contact.com',
    privacy: 'privacy@bpo-contact.com',
    morocco: 'contact@bpo-contact.com',
  },

  phone: {
    main: '+212 5 37 37 37 77',
    support: '+212 5 37 37 37 77',
    morocco: '+212 5 37 37 37 77',
    whatsapp: '212658380381',
  },

  // Social media
  social: {
    twitter: 'https://twitter.com/bpocontact',
    facebook: 'https://facebook.com/bpocontact',
    instagram: 'https://instagram.com/bpocontact',
    github: 'https://github.com/bpo-contact',
  },

  // Legal
  copyright: `© ${new Date().getFullYear()} BPO Contact. All rights reserved.`,
  copyrightYear: new Date().getFullYear(),

  // Locations
  locations: {
    headquarters: {
      city: 'Casablanca',
      country: 'Morocco',
      timezone: 'GMT+1',
      address: 'Casablanca, Morocco',
    },
  },
  // Markets served remotely from the Casablanca headquarters (no local office)
  marketsServed: {
    europe: ['Madrid, Spain', 'Spain', 'Europe'],
  },

  // Brand Colors (JS values mirroring CSS tokens)
  colors: {
    navy: '#1E5FFF',        // primary bright royal blue
    navyDeep: '#003FBD',    // darker shade
    navyLight: '#5B8FFF',   // lighter shade
    coral: '#FF5A36',       // vibrant coral/orange CTA accent
    coralLight: '#FF7A5C',
    coralDark: '#E2431F',
  },

  // Products / Service lines
  products: {
    customerCare: 'Customer Care',
    techSupport: 'Technical Support',
    backOffice: 'Back-Office Services',
    itSoftware: 'IT & Software Development',
  },

  // SEO
  seo: {
    title:
      'BPO Contact | Multilingual Contact Center & CX Outsourcing',
    titleTemplate: '%s | BPO Contact',
    description:
      "Your multilingual contact center. Customer service, technical support & back-office in multiple languages. Casablanca HQ.",
    keywords: [
      'contact center outsourcing',
      'multilingual call center',
      'customer experience outsourcing',
      'CX outsourcing',
      'technical support outsourcing',
      'back-office services',
      'BPO Contact'
    ],
  },
} as const;

// Helper function to get brand-related text
export function getBrandText(key: keyof typeof BRAND): string {
  return String(BRAND[key]);
}

// Export type for TypeScript usage
export type BrandConfig = typeof BRAND;

export default BRAND;
